import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Download, FileText, Gauge, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { generateQuotePDF } from "../utils/pdfGenerator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicQuote() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const res = await fetch(`${API}/public/quote/${token}`);
        if (res.ok) {
          setData(await res.json());
        } else {
          setError(true);
        }
      } catch (e) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchQuote();
  }, [token]);

  const downloadPDF = () => {
    if (!data) return;
    const doc = generateQuotePDF(data.quote, data.workshop);
    doc.save(`Preventivo_${data.quote.quote_number}.pdf`);
  };

  const vehicleName = () => {
    const q = data?.quote || {};
    const b = (q.vehicle_brand || "").trim();
    const m = (q.vehicle_model || "").trim();
    let name = (b && m && m.toLowerCase().startsWith(b.toLowerCase())) ? m : `${b} ${m}`.trim();
    if (q.vehicle_version) name = `${name} ${q.vehicle_version}`.trim();
    return name || "-";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-zinc-950 text-center px-6" data-testid="public-quote-error">
        <AlertTriangle className="w-16 h-16 text-red-500 mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2 font-['Barlow_Condensed'] uppercase">Preventivo non trovato</h1>
        <p className="text-zinc-400">Il link potrebbe essere scaduto o non valido.</p>
      </div>
    );
  }

  const q = data.quote;
  const w = data.workshop || {};

  return (
    <div className="min-h-screen bg-zinc-950 text-white py-8 px-4" data-testid="public-quote-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center text-white font-bold text-2xl">P</div>
            <div>
              <p className="text-lg font-bold uppercase font-['Barlow_Condensed'] leading-tight">{w.company_name || "PServicePro"}</p>
              {w.phone && <p className="text-xs text-zinc-400">Tel: {w.phone}</p>}
            </div>
          </div>
          <span className="px-3 py-1 rounded-full bg-primary/20 text-primary text-sm font-bold">
            Preventivo #{q.quote_number}
          </span>
        </div>

        {/* Card */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-zinc-500">Cliente</p>
              <p className="text-white font-medium">{q.customer_name}</p>
            </div>
            <div>
              <p className="text-zinc-500">Veicolo</p>
              <p className="text-white font-medium">{vehicleName()}</p>
            </div>
            <div>
              <p className="text-zinc-500">Targa</p>
              <p className="text-white font-mono">{q.vehicle_plate}</p>
            </div>
            {q.vehicle_km > 0 && (
              <div>
                <p className="text-zinc-500 flex items-center gap-1"><Gauge className="w-3 h-3" /> Km</p>
                <p className="text-white font-mono">{q.vehicle_km.toLocaleString()}</p>
              </div>
            )}
          </div>

          {/* Items */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-zinc-500 border-b border-zinc-800 text-left">
                  <th className="py-2">Descrizione</th>
                  <th className="py-2 text-center">Qtà</th>
                  <th className="py-2 text-right">Prezzo</th>
                  <th className="py-2 text-right">Totale</th>
                </tr>
              </thead>
              <tbody>
                {(q.items || []).map((item, i) => (
                  <tr key={i} className="border-b border-zinc-800/50">
                    <td className="py-2 text-white">{item.description}</td>
                    <td className="py-2 text-center font-mono">{item.quantity}</td>
                    <td className="py-2 text-right font-mono">€ {(item.unit_price || 0).toFixed(2)}</td>
                    <td className="py-2 text-right font-mono">€ {(item.subtotal || (item.quantity * item.unit_price) || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-zinc-400">
              <span>Imponibile</span>
              <span className="font-mono">€ {(q.subtotal || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-zinc-400">
              <span>IVA</span>
              <span className="font-mono">€ {(q.vat_total || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold pt-2 border-t border-zinc-800">
              <span>TOTALE</span>
              <span className="text-primary font-mono">€ {(q.total || 0).toFixed(2)}</span>
            </div>
          </div>

          <p className="text-xs text-zinc-500 text-center">Validità preventivo: {q.validity_days || 30} giorni</p>

          {/* Download */}
          <Button
            onClick={downloadPDF}
            data-testid="public-download-pdf-btn"
            className="w-full btn-primary"
          >
            <Download className="w-5 h-5 mr-2" />
            Scarica Preventivo PDF
          </Button>
        </div>

        <p className="text-center text-xs text-zinc-600 mt-6 flex items-center justify-center gap-1">
          <FileText className="w-3 h-3" /> Documento generato da PServicePro
        </p>
      </div>
    </div>
  );
}
